import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/features/compliance/queryKeys'
import {
  bulkUpdateAssetReview,
  createComplianceJob,
  deleteComplianceJob,
  fetchComplianceAssets,
  fetchComplianceJobStatus,
  fetchComplianceJobs,
  fetchComplianceLogs,
  fetchTrialBalanceAvailability,
  fetchXeroTrialBalance,
  reanalyzeJob,
  updateAssetReview,
  uploadJobFiles,
} from '@/features/compliance/api'
import type { AssetReviewStatus, UpdateAssetReviewRequest } from '@/features/compliance/types'

export function useTrialBalanceAvailability(clientId: string) {
  return useQuery({
    queryKey: queryKeys.trialBalanceAvailability(clientId),
    queryFn: () => fetchTrialBalanceAvailability(clientId),
    enabled: !!clientId,
  })
}

type ComplianceJobsQuery = { state: { data: { items: { status: string }[] } | undefined } }

export function useComplianceJobs(
  clientId: string,
  skip: number,
  take: number,
  orgId: string,
  options?: { refetchInterval?: number | false | ((query: ComplianceJobsQuery) => number | false); enabled?: boolean }
) {
  const enabled = !!clientId && (options?.enabled ?? true)
  return useQuery({
    queryKey: queryKeys.jobs(clientId, skip, take, orgId),
    queryFn: () => fetchComplianceJobs(clientId, skip, take, orgId),
    enabled,
    refetchInterval: options?.refetchInterval,
  })
}

type JobStatusQuery = { state: { data: { status: string } | undefined } }

export function useComplianceJobStatus(
  clientId: string,
  jobId: string,
  options?: { refetchInterval?: number | ((query: JobStatusQuery) => number | false) }
) {
  return useQuery({
    queryKey: queryKeys.jobStatus(clientId, jobId),
    queryFn: () => fetchComplianceJobStatus(clientId, jobId),
    enabled: !!clientId && !!jobId,
    refetchInterval: options?.refetchInterval,
  })
}

export function useCreateComplianceJobMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { periodEndDate: string; orgId?: string | null }) =>
      createComplianceJob(clientId, params.periodEndDate, params.orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance', 'jobs', clientId] })
    },
  })
}

export function useDeleteComplianceJobMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (jobId: string) => deleteComplianceJob(clientId, jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance', 'jobs', clientId] })
    },
  })
}

export function useXeroTrialBalance(clientId: string, jobId: string) {
  return useQuery({
    queryKey: queryKeys.xeroTrialBalance(clientId, jobId),
    queryFn: () => fetchXeroTrialBalance(clientId, jobId),
    enabled: !!clientId && !!jobId,
  })
}

export function useComplianceAssets(clientId: string, jobId: string) {
  return useQuery({
    queryKey: queryKeys.complianceAssets(clientId, jobId),
    queryFn: () => fetchComplianceAssets(clientId, jobId),
    enabled: !!clientId && !!jobId,
  })
}

type ComplianceLogsQuery = { state: { data: { status: string } | undefined } }

export function useComplianceLogs(
  clientId: string,
  jobId: string,
  options?: { refetchInterval?: number | false | ((query: ComplianceLogsQuery) => number | false) }
) {
  return useQuery({
    queryKey: queryKeys.complianceLogs(clientId, jobId),
    queryFn: () => fetchComplianceLogs(clientId, jobId),
    enabled: !!clientId && !!jobId,
    refetchInterval: options?.refetchInterval,
  })
}

export function useUploadJobFilesMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: {
      jobId: string
      indexFile?: File | null
      workpaperFiles: File[]
      orgId?: string
      trialBalanceSource?: string
    }) => uploadJobFiles(clientId, payload),
    onSuccess: (_, variables) => {
      const jobId = variables.jobId
      queryClient.invalidateQueries({ queryKey: queryKeys.jobStatus(clientId, jobId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceLogs(clientId, jobId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceAssets(clientId, jobId) })
    },
  })
}

export function useUpdateAssetReviewMutation(clientId: string, jobId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { complianceAssetId: string; data: UpdateAssetReviewRequest }) =>
      updateAssetReview(clientId, jobId, payload.complianceAssetId, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceAssets(clientId, jobId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.jobStatus(clientId, jobId) })
    },
  })
}

export function useBulkUpdateAssetReviewMutation(clientId: string, jobId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: {
      complianceAssetIds: string[]
      status?: AssetReviewStatus | null
      notes?: string | null
    }) => bulkUpdateAssetReview(clientId, jobId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceAssets(clientId, jobId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.jobStatus(clientId, jobId) })
    },
  })
}

export function useReanalyzeJobMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { jobId: string; workpaperFiles: File[]; orgId?: string }) =>
      reanalyzeJob(clientId, payload),
    onSuccess: (_, variables) => {
      const jobId = variables.jobId
      queryClient.invalidateQueries({ queryKey: queryKeys.jobStatus(clientId, jobId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceLogs(clientId, jobId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.complianceAssets(clientId, jobId) })
    },
  })
}
